import React, { useState, useEffect } from 'react';
import MicroLearningModal from '../MicroLearningModal/index';
import SeedRewardBadge from '../SeedRewardBadge/index';
import { LearningModule, UserModuleProgress } from '../types';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

// Floating particles component for visual interest
const FloatingParticles: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg">
    {[...Array(6)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-emerald-400/60"
        initial={{
          x: `${15 + i * 15}%`,
          y: '100%',
          opacity: 0,
          scale: 0
        }}
        animate={{
          y: [null, '-20%'],
          opacity: [0, 0.8, 0],
          scale: [0, 1, 0.5]
        }}
        transition={{
          duration: 3 + i * 0.5,
          repeat: Infinity,
          delay: i * 0.8,
          ease: 'easeOut'
        }}
      />
    ))}
  </div>
);

// Shimmer effect for the top bar
const ShimmerBar: React.FC = () => (
  <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-500 rounded-t-lg relative overflow-hidden">
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
      animate={{ x: ['-100%', '100%'] }}
      transition={{
        duration: 2,
        repeat: Infinity,
        repeatDelay: 3,
        ease: 'easeInOut'
      }}
    />
  </div>
);

const SeriesTitleTooltip: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div
      className="relative inline-block flex items-center"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg bottom-full left-0 mb-1 whitespace-nowrap">
          {title}
          <div className="absolute top-full left-2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

// Completed module card component
const CompletedModuleCard: React.FC<{
  module: LearningModule;
  userProgress?: UserModuleProgress;
  onDismiss?: () => void;
}> = ({ module, userProgress, onDismiss }) => {
  const score = userProgress?.score || 0;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="w-full rounded-lg bg-white shadow-md overflow-visible"
    >
      {/* Green top bar to distinguish learning cards */}
      <div className="h-1 bg-green-500 rounded-t-lg"></div>

      <div className="p-2.5 sm:p-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1.5 sm:gap-0 mb-2">
          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            <SeriesTitleTooltip title={module.seriesTitle || ''}>
              <span className="text-[10px] sm:text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full truncate max-w-[100px] sm:max-w-[120px]">
                {module.seriesTitle}
              </span>
            </SeriesTitleTooltip>

            <span className="text-[10px] sm:text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
              ✓ Completed
            </span>

            <span className="text-[10px] sm:text-xs text-02-dim-gray">{module.estimatedTime}</span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1">
              <span className="text-[10px] sm:text-xs font-medium text-green-700">{score}%</span>
              <svg
                className="w-3 h-3 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            {onDismiss && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
                className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <svg
                  className="w-4 h-4 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-jet-black mb-1">
              {module.title}
            </h3>
            <p className="text-xs sm:text-sm text-02-dim-gray mb-1.5 sm:mb-2 line-clamp-2">
              {module.description}
            </p>
            <div className="flex items-center flex-wrap gap-1 sm:gap-1.5">
              {module.relatedTopics?.slice(0, 3).map((topic, i) => (
                <span
                  key={i}
                  className="text-[10px] text-green-700 bg-green-50 px-1.5 sm:px-2 py-0.5 rounded-[80px] font-semibold whitespace-nowrap"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden sm:flex flex-col justify-center items-center flex-shrink-0">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
              className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-1"
            >
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
            <span className="text-[10px] font-semibold text-green-900">Complete</span>
          </div>
        </div>

        {/* Completion celebration */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-3 pt-3 border-t border-green-100"
        >
          <div className="flex items-center justify-between text-xs text-green-700">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Completed!
            </span>
            <span className="font-medium">{score}% Score</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Failed module card component (for when user fails quiz)
const FailedModuleCard: React.FC<{
  module: LearningModule;
  userProgress?: UserModuleProgress;
  onDismiss?: () => void;
}> = ({ module, userProgress, onDismiss }) => {
  const score = userProgress?.score || 0;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="w-full rounded-lg bg-white shadow-md overflow-visible"
    >
      {/* Orange top bar to distinguish failed cards */}
      <div className="h-1 bg-orange-500 rounded-t-lg"></div>

      <div className="p-2.5 sm:p-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1.5 sm:gap-0 mb-2">
          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            <SeriesTitleTooltip title={module.seriesTitle || ''}>
              <span className="text-[10px] sm:text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full truncate max-w-[100px] sm:max-w-[120px]">
                {module.seriesTitle}
              </span>
            </SeriesTitleTooltip>

            <span className="text-[10px] sm:text-xs font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full">
              Keep Trying!
            </span>

            <span className="text-[10px] sm:text-xs text-02-dim-gray">{module.estimatedTime}</span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1">
              <span className="text-[10px] sm:text-xs font-medium text-orange-700">{score}%</span>
              <svg
                className="w-3 h-3 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            {onDismiss && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
                className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <svg
                  className="w-4 h-4 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-jet-black mb-1">
              {module.title}
            </h3>
            <p className="text-xs sm:text-sm text-02-dim-gray mb-1.5 sm:mb-2 line-clamp-2">
              {module.description}
            </p>
            <div className="flex items-center flex-wrap gap-1 sm:gap-1.5">
              {module.relatedTopics?.slice(0, 3).map((topic, i) => (
                <span
                  key={i}
                  className="text-[10px] text-orange-700 bg-orange-50 px-1.5 sm:px-2 py-0.5 rounded-[80px] font-semibold whitespace-nowrap"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden sm:flex flex-col justify-center items-center flex-shrink-0">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
              className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mb-1"
            >
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </motion.div>
            <span className="text-[10px] font-semibold text-orange-900">Try Again</span>
          </div>
        </div>

        {/* Encouragement section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-3 pt-3 border-t border-orange-100"
        >
          <div className="flex items-center justify-between text-xs text-orange-700">
            <span className="flex items-center gap-1">
              <SeriesTitleTooltip title="This module will appear in your feed again in a few minutes so you can try again!">
                <svg
                  className="w-4 h-4 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </SeriesTitleTooltip>
              Keep Learning!
            </span>
            <span className="font-medium">{score}% - Try Again Later</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

interface MicroLearningCardProps {
  module: LearningModule;
  userProgress?: UserModuleProgress;
  onModuleClick?: (module: LearningModule) => void;
  // Lifecycle callbacks for intelligent injection tracking
  onStart?: () => void;
  onComplete?: (score: number, quizPassed: boolean, timeSpent: number) => void;
  onAbandon?: () => void;
  onClose?: () => void;
}

const MicroLearningCard: React.FC<MicroLearningCardProps> = ({
  module,
  userProgress,
  onModuleClick,
  onStart,
  onComplete,
  onAbandon,
  onClose
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCompleted, setIsCompleted] = useState(userProgress?.status === 'COMPLETED');
  const [isFailed, setIsFailed] = useState(false);
  const [moduleWasCompleted, setModuleWasCompleted] = useState(false);
  const [completionData, setCompletionData] = useState<{
    score: number;
    passed: boolean;
    timeSpent: number;
  } | null>(null);

  // Use ref to store completion data immediately (doesn't rely on state updates)
  const completionDataRef = React.useRef<{
    score: number;
    passed: boolean;
    timeSpent: number;
  } | null>(null);

  // Listen for module completion events
  useEffect(() => {
    const handleModuleCompleted = (event: CustomEvent) => {
      if (event.detail.moduleId === module.id) {
        console.log('[MicroLearningCard] 🎉 Module completed event received:', event.detail);
        setModuleWasCompleted(true);
        // Store the completion data (score, passed status, etc.)
        const completionInfo = {
          score: event.detail.score || 0,
          passed: event.detail.passed || false,
          timeSpent: event.detail.timeSpent || 0
        };
        setCompletionData(completionInfo);
        completionDataRef.current = completionInfo; // Store in ref immediately

        // Call onComplete callback if provided
        if (onComplete) {
          console.log('[MicroLearningCard] Calling onComplete callback');
          onComplete(completionInfo.score, completionInfo.passed, completionInfo.timeSpent);
        }
        // Don't immediately set isCompleted - wait for modal close
      }
    };

    const handleQuizFailed = (event: CustomEvent) => {
      if (event.detail.moduleId === module.id && event.detail.passed === false) {
        console.log('[MicroLearningCard] ⚠️ Quiz failed event received:', event.detail);
        // Store the failure data with score
        const failureInfo = {
          score: event.detail.score || 0,
          passed: false,
          timeSpent: event.detail.timeSpent || 0
        };
        setCompletionData(failureInfo);
        completionDataRef.current = failureInfo; // Store in ref immediately
        setModuleWasCompleted(true); // Mark that module outcome is determined

        // Call onComplete callback even for failures (with passed=false)
        if (onComplete) {
          console.log('[MicroLearningCard] Calling onComplete callback for failure');
          onComplete(failureInfo.score, failureInfo.passed, failureInfo.timeSpent);
        }
      }
    };

    window.addEventListener('moduleCompleted', handleModuleCompleted as EventListener);
    window.addEventListener('quizFailed', handleQuizFailed as EventListener);

    return () => {
      window.removeEventListener('moduleCompleted', handleModuleCompleted as EventListener);
      window.removeEventListener('quizFailed', handleQuizFailed as EventListener);
    };
  }, [module.id, onComplete]);

  useEffect(() => {
    if (userProgress?.status === 'COMPLETED') {
      setIsCompleted(true);
      setIsFailed(false);
    }
  }, [userProgress]);

  // Handle modal close - check if we should show completed/failed state
  const handleModalClose = () => {
    setIsModalOpen(false);

    // Use REF for completion data - state may not have updated yet due to React batching
    const completion = completionDataRef.current;

    if (completion) {
      console.log('[MicroLearningCard] Has completion data from ref:', completion);
      if (completion.passed) {
        setIsCompleted(true);
        setModuleWasCompleted(false);
        console.log(
          '[MicroLearningCard] ✅ Module completed (passed) - showing CompletedModuleCard'
        );
      } else {
        setIsFailed(true);
        console.log('[MicroLearningCard] ❌ Module completed (failed) - showing FailedModuleCard');
      }
      // DON'T call onClose - keep the card visible so user can see completion/failed state
    } else if (moduleWasCompleted) {
      // Fallback: if moduleWasCompleted is true but no completionData
      setIsCompleted(true);
      setModuleWasCompleted(false);
      console.log(
        '[MicroLearningCard] ✅ Module completed (fallback) - showing CompletedModuleCard'
      );
      // DON'T call onClose
    } else {
      // Module was abandoned (closed without completion)
      // Only call onAbandon for tracking - DO NOT call onClose
      // The card should remain visible so user can try again
      console.log(
        '[MicroLearningCard] 🚪 Module abandoned - calling onAbandon only, keeping card visible'
      );
      if (onAbandon) {
        onAbandon();
      }
      // DO NOT call onClose here - user just closed the modal, they didn't dismiss the card
    }
  };

  const handleCardClick = async () => {
    if (isCompleted || isFailed) {
      // Don't open modal for completed/failed cards
      return;
    }

    if (onModuleClick) {
      onModuleClick(module);
    } else {
      // For feed cards, we need to fetch full module content before opening modal
      setIsModalOpen(true);
      // Reset completion state when opening modal (in case user reopens before completing)
      if (moduleWasCompleted) {
        setModuleWasCompleted(false);
      }
      // Clear any old completion data
      setCompletionData(null);
      completionDataRef.current = null;

      // Call onStart callback when module is opened
      if (onStart) {
        onStart();
      }
    }
  };

  const getStatusText = () => {
    if (!userProgress) return null;

    switch (userProgress.status) {
      case 'COMPLETED':
        return userProgress.isRestarted ? 'Completed (Restarted)' : 'Completed';
      case 'IN_PROGRESS':
        // Show module progress in series if available
        if (
          userProgress.currentModuleNumber !== undefined &&
          userProgress.totalModulesInSeries !== undefined
        ) {
          return `Module ${userProgress.currentModuleNumber} of ${userProgress.totalModulesInSeries}`;
        }
        return 'In Progress';
      case 'NOT_STARTED':
        if (
          userProgress.currentModuleNumber !== undefined &&
          userProgress.totalModulesInSeries !== undefined
        ) {
          return `Module ${userProgress.currentModuleNumber} of ${userProgress.totalModulesInSeries}`;
        }
        return 'Not Started';
      default:
        return null;
    }
  };

  const getDetailedProgressText = () => {
    if (!userProgress) return null;

    if (
      userProgress.completedModulesInSeries !== undefined &&
      userProgress.totalModulesInSeries !== undefined
    ) {
      return `${userProgress.completedModulesInSeries} of ${userProgress.totalModulesInSeries} modules completed`;
    }

    return null;
  };

  console.log('userProgress', userProgress);
  console.log('completionData', completionData);

  // Create updated userProgress with the latest completion data
  const updatedUserProgress = completionData
    ? { ...userProgress, score: completionData.score, timeSpent: completionData.timeSpent }
    : userProgress;

  // Render completed or failed card if module is completed/failed, otherwise render normal card
  if (isCompleted) {
    console.log('[MicroLearningCard] 🟢 Rendering CompletedModuleCard');
    return (
      <>
        <CompletedModuleCard
          module={module}
          userProgress={updatedUserProgress}
          onDismiss={onClose}
        />

        {isModalOpen && (
          <MicroLearningModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            moduleId={module.id}
          />
        )}
      </>
    );
  }

  if (isFailed) {
    console.log('[MicroLearningCard] 🟠 Rendering FailedModuleCard');
    return (
      <>
        <FailedModuleCard module={module} userProgress={updatedUserProgress} onDismiss={onClose} />

        {isModalOpen && (
          <MicroLearningModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            moduleId={module.id}
          />
        )}
      </>
    );
  }

  console.log('[MicroLearningCard] ⚪ Rendering normal card');
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.01, y: -2 }}
        transition={{ duration: 0.2 }}
        className="relative w-full cursor-pointer group"
        onClick={handleCardClick}
      >
        {/* Animated glow effect behind the card */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-400 via-green-300 to-emerald-400 rounded-xl opacity-0 group-hover:opacity-70 blur-sm transition-opacity duration-500" />
        <motion.div
          className="absolute -inset-0.5 bg-gradient-to-r from-emerald-400/30 via-green-300/30 to-emerald-400/30 rounded-xl blur-sm"
          animate={{
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        {/* Main card */}
        <div className="relative w-full rounded-lg bg-white shadow-lg overflow-visible ring-1 ring-emerald-100">
          {/* Floating particles */}
          <FloatingParticles />

          {/* Shimmer top bar */}
          <ShimmerBar />

          <div className="p-2.5 sm:p-3 relative">
            {/* Learning badge indicator */}
            <motion.div
              className="absolute -top-3 right-3 flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md"
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <Sparkles className="w-2.5 h-2.5" />
              <span>LEARN</span>
            </motion.div>

            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <SeriesTitleTooltip title={module.seriesTitle || ''}>
                <span className="text-[10px] sm:text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full truncate max-w-[200px] sm:max-w-[280px] border border-emerald-100">
                  {module.seriesTitle}
                </span>
              </SeriesTitleTooltip>
              {getStatusText() && (
                <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {getStatusText()}
                </span>
              )}
              <span className="text-[10px] sm:text-xs text-gray-500">{module.estimatedTime}</span>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                  {module.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2 line-clamp-2">
                  {module.description}
                </p>
              </div>
              <div className="hidden sm:flex flex-col justify-center items-center flex-shrink-0">
                <motion.div
                  className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mb-1 ring-2 ring-emerald-200/50 shadow-md"
                  animate={{
                    boxShadow: [
                      '0 0 0 0px rgba(16, 185, 129, 0)',
                      '0 0 0 4px rgba(16, 185, 129, 0.15)',
                      '0 0 0 0px rgba(16, 185, 129, 0)'
                    ]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    times: [0, 0.5, 1]
                  }}
                >
                  <img
                    src="https://max-esmail.s3.us-east-2.amazonaws.com/learning-icon.jpg"
                    alt="Learn"
                    className="w-9 h-9 rounded-full"
                  />
                </motion.div>
                <span className="text-[10px] font-bold text-emerald-700">Learn</span>
              </div>
            </div>

            {/* Progress section */}
            {userProgress && (
              <div className="mt-2 pt-2 border-t border-emerald-100">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex flex-col">
                    <span className="text-[10px] sm:text-xs text-gray-600 font-medium">
                      Series Progress
                    </span>
                    {getDetailedProgressText() && (
                      <span className="text-[10px] text-gray-500 mt-0.5">
                        {getDetailedProgressText()}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {userProgress.completedModulesInSeries !== undefined &&
                      userProgress.totalModulesInSeries !== undefined && (
                        <div className="text-[10px] sm:text-xs text-emerald-600 font-bold">
                          {userProgress.completedModulesInSeries + 1} of{' '}
                          {userProgress.totalModulesInSeries}
                        </div>
                      )}
                  </div>
                </div>
                <div className="w-full bg-emerald-100 rounded-full h-1.5 overflow-hidden">
                  <motion.div
                    className="h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-400"
                    initial={{ width: 0 }}
                    animate={{
                      width:
                        userProgress.completedModulesInSeries !== undefined &&
                        userProgress.totalModulesInSeries !== undefined
                          ? `${Math.round((userProgress.completedModulesInSeries / userProgress.totalModulesInSeries) * 100)}%`
                          : '0%'
                    }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </div>
              </div>
            )}

            {/* Call to action with seed reward */}
            <div className="mt-3 pt-2 border-t border-emerald-100 flex items-center justify-between">
              <motion.div
                className="flex items-center gap-1.5 text-[10px] sm:text-xs text-emerald-600 font-medium"
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <span className="hidden sm:inline">Click to start</span>
                <span className="sm:hidden">Tap to start</span>
                <span>→</span>
              </motion.div>

              {module.seedReward && module.seedReward > 0 && (
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: [0.9, 1, 0.9] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <SeedRewardBadge seedReward={module.seedReward} />
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {isModalOpen && (
        <MicroLearningModal
          isOpen={isModalOpen}
          onClose={() => handleModalClose()}
          moduleId={module.id}
        />
      )}
    </>
  );
};

export default MicroLearningCard;
